// Theme Configuration System
// Preserves exact current animation behavior while allowing theme switching

export const THEMES = {
  HORROR_CLASSIC: {
    id: 'horror_classic',
    name: 'Horror Classic',
    description: 'Flying bats and Mothman with swirling mist - current working implementation',
    titleIcon: {
      type: 'emoji',
      value: '🎃'
    },
    background: {
      gradient: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f0f23 100%)',
      mistEffect: true
    },
    creatures: {
      bats: {
        enabled: true,
        image: '/Bat.webp',
        spawnChance: 0.85,
        spawnInterval: { min: 1500, max: 4000 }, // 1.5-4 seconds
        speed: { min: 6.0, max: 10.0 },
        size: { min: 0.8, max: 1.2 },
        lifespan: 12000,
        opacity: 0.8,
        filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5)) brightness(0.7)',
        flutter: { amplitude: 15, frequency: 0.01 },
        wobble: { amplitude: 30, frequency: 0.005 }
      },
      mothman: {
        enabled: true,
        image: '/Mothman.webp',
        spawnChance: 0.6,
        spawnInterval: { min: 15000, max: 30000 }, // 15-30 seconds
        speed: 30.0,
        scale: 4.0,
        lifespan: 30000,
        opacity: 0.7,
        filter: 'drop-shadow(0 0 6px rgba(255,0,0,0.4)) brightness(0.8) contrast(1.1)',
        swooping: {
          primaryArc: 120,
          secondaryFlutter: 30,
          intensityVariation: { min: 0.2, max: 1.0 }
        }
      }
    }
  },

  DARK_FOREST: {
    id: 'dark_forest',
    name: 'Dark Forest',
    description: 'Sinister foggy forest with glowing eyes watching from the shadows',
    titleIcon: {
      type: 'image',
      value: '/Spookytree.webp'
    },
    background: {
      gradient: 'radial-gradient(ellipse at center, #0a0f0a 0%, #1a2e1a 35%, #0f1a0f 70%, #050a05 100%)',
      mistEffect: true,
      forestMist: true
    },
    creatures: {
      bats: { enabled: false }, // No bats in forest
      mothman: { enabled: false }, // No mothman in forest
      eyes: {
        enabled: true,
        spawnChance: 0.7,
        spawnInterval: { min: 2000, max: 6000 }, // 2-6 seconds
        colors: ['#ff4444', '#44ff44', '#ffff44', '#ff8844', '#8844ff'],
        size: { min: 0.8, max: 1.5 },
        lifespan: 8000, // 8 seconds
        opacity: { min: 0.6, max: 0.9 },
        glowIntensity: { min: 8, max: 15 },
        blinkChance: 0.3, // 30% chance to blink
        maxEyes: 8 // Maximum eyes on screen at once
      },
      vegetation: {
        enabled: false // Disabled - keeping eyes and fog only
      }
    }
  },

  ANCIENT_TOMB: {
    id: 'ancient_tomb',
    name: 'Ancient Tomb',
    description: 'Walking through a mysterious ancient tomb with video atmosphere and shadowy effects',
    titleIcon: {
      type: 'emoji',
      value: '🏺'
    },
    background: {
      gradient: 'radial-gradient(ellipse at center, #1a1a1a 0%, #2d2d2d 20%, #1a1a1a 40%, #0d0d0d 60%, #1a1a1a 80%, #000000 100%)',
      mistEffect: true,
      tombWalking: true,
      videoBackground: {
        desktop: '/Tomb-animation.mp4',
        mobile: '/Tomb-animation-mobile.mp4'
      }
    },
    creatures: {
      bats: { enabled: false },
      mothman: { enabled: false },
      eyes: { enabled: false },
      tombShadows: {
        enabled: true,
        density: { min: 8, max: 15 }, // Number of shadow systems
        shadowsPerSystem: { min: 3, max: 6 },
        speed: { min: 1, max: 3 }, // Slow, eerie movement
        size: { min: 20, max: 60 },
        opacity: { min: 0.1, max: 0.3 },
        colors: ['#1a1a1a', '#2d2d2d', '#0d0d0d', '#333333', '#262626', '#0a0a0a'],
        flickerIntensity: { min: 1, max: 3 },
        walkingEffect: { enabled: true, intensity: 2 },
        lifespan: { min: 5000, max: 12000 }
      },
      sarcophagus: {
        enabled: true,
        image: '/Sarcophagus.webp',
        spawnChance: 0.3,
        spawnInterval: { min: 20000, max: 45000 }, // 20-45 seconds
        // A creature dies at whichever comes first, age or leaving the
        // viewport. At the original 0.5 (10px/sec) with a 25s lifespan this
        // managed 250px and then evaporated in mid-air - it had never once
        // completed a pass.
        //
        // Entries from the top and bottom move diagonally at HALF speed, so
        // they are what the lifespan has to cover: 1380px of vertical travel
        // at 16px/sec is ~86s, against ~66s for a horizontal crossing.
        speed: 1.6, // still a slow, ominous drift - about a minute per screen
        scale: 2.5,
        lifespan: 100000,
        opacity: 0.4,
        filter: 'drop-shadow(0 0 8px rgba(139, 69, 19, 0.6)) brightness(0.6) contrast(1.2)',
        positioning: {
          startSide: 'random', // Can appear from any side
          movement: 'drift' // Slow drifting movement
        }
      },

      // The mummy: an animated GIF figure shambling along the ground band.
      // The browser plays each GIF's own frames; the loop only moves the box
      // and swaps the file when the figure turns. One file per facing; a
      // missing facing falls back to its mirror image, and a missing quarter
      // turn to the straight-on file.
      //
      // Mid-crossing it may stop, turn to face the room, stare, and carry on
      // the way it was going - `pause`. The turn is a quarter-step through the
      // diagonal file each way. Position is held, not re-derived, so it
      // resumes from exactly where it stopped.
      //
      // speed is px per 50ms tick: 1.8 is 36px/sec, about a minute across a
      // 1920 screen. The GIFs cycle in 1.6s (8 frames at 200ms), so each
      // stride covers ~58px against a ~120px figure - feet that grip rather
      // than skate. Lifespan must outlive the widest crossing plus the longest
      // pause: 2960px at 36px/sec is ~82s, plus under 4s standing still.
      walker: {
        enabled: true,
        images: {
          west: '/MummyWalkWest.gif',
          east: '/MummyWalkEast.gif',
          south: '/MummyWalkSouth.gif',
          southWest: '/MummyWalkSouthWest.gif',
          southEast: '/MummyWalkSouthEast.gif'
        },
        // The spawner wakes every 15s and starts one 60% of the time, if
        // none is on screen: about 25s of expected waiting between crossings.
        spawnChance: 0.6,
        spawnInterval: { min: 15000, max: 15000 },
        speed: 1.8,
        scale: 1.0,
        y: { min: 0.55, max: 0.78 }, // band of the viewport, as a fraction
        opacity: 0.95,
        pause: {
          chance: 0.7,                      // per crossing
          hold: { min: 1500, max: 3000 },   // ms spent facing the room
          turn: 350                         // ms on each diagonal, each way
        },
        lifespan: 100000
      }
    }
  },

  // A derelict outdoor cinema at night: the screen still glowing, moths still
  // circling the beam, and the cars in the lot that never left.
  //
  // What is left running is deliberately small: moths, headlights and the
  // parked lot. The roaming sprites were retired once the lot filled the
  // bottom of the frame - see each slot for why. Their config is kept rather
  // than deleted, so any of them can come back without re-deriving its tuning.
  //
  // This theme also restyles the interface itself, in App.css under
  // [data-theme="haunted_drive_in"]. Artwork spec: docs/THEME_ASSET_BRIEF.md.
  HAUNTED_DRIVE_IN: {
    id: 'haunted_drive_in',
    name: 'Haunted Drive-In',
    description: 'An abandoned drive-in where the picture never stopped playing',
    titleIcon: {
      type: 'image',
      value: '/DriveInSpeaker.webp'
    },
    background: {
      // Brightest in the middle, so it reads as the glow of the screen. Kept
      // cool and near-neutral rather than purple - a night sky over a lot, not
      // a haunted heath.
      gradient: 'radial-gradient(ellipse at center, #11151e 0%, #0a0d15 32%, #05070d 66%, #020305 100%)',
      mistEffect: true
    },
    creatures: {
      // Moths round the projector beam. Slower and more erratic than bats, and
      // lit rather than darkened - they are the one thing near the light.
      bats: {
        enabled: true,
        image: '/Moth.webp',
        spawnChance: 0.9,
        spawnInterval: { min: 1000, max: 3000 },
        speed: { min: 4.0, max: 7.0 },
        size: { min: 0.5, max: 0.9 },
        lifespan: 10000,
        opacity: 0.85,
        filter: 'drop-shadow(0 0 6px rgba(255,220,150,0.5)) brightness(1.05)',
        // Opt-in, so the classic bats keep their exact sine-wave glide. Moths
        // wander instead: no fixed heading, pace that surges and stalls.
        flight: 'erratic',
        // Horizontal squash standing in for a wingbeat. min is how far the
        // wings close - 0.45 reads as edge-on without vanishing.
        wingFlap: { min: 0.45 },
        flutter: { amplitude: 25, frequency: 0.02 },
        wobble: { amplitude: 20, frequency: 0.008 }
      },

      // The saucer. Retired: with the lot filling the bottom of the frame the
      // theme no longer needs a set piece crossing it. Config kept so it can
      // be switched back on without re-deriving the tuning.
      mothman: {
        enabled: false,
        image: '/Saucer.webp',
        spawnChance: 0.65,
        spawnInterval: { min: 10000, max: 18000 },
        speed: 22.0,
        scale: 3.0,
        lifespan: 25000,
        opacity: 0.75,
        filter: 'drop-shadow(0 0 10px rgba(120,255,180,0.5)) brightness(0.9) contrast(1.1)',
        swooping: {
          primaryArc: 100,
          secondaryFlutter: 15, // saucers glide; less flutter than a creature
          intensityVariation: { min: 0.3, max: 0.9 }
        }
      },

      // The lot itself: two rows of cars parked facing the screen, so only
      // their backs and tail lights show. Drawn in code and completely still -
      // they are parked, and they never touch the animation loop.
      parkedCars: {
        enabled: true,
        rows: 2,
        width: { min: 110, max: 190 },
        // Lifted just clear of the foot of the gradient (#020305) - enough for
        // the silhouettes to read, not enough to draw the eye. Tuned twice:
        // near-black at first, which left only floating red dots, then too
        // light once the sky was darkened, which turned the lot into a row of
        // bright boxes.
        colors: ['#0e1119', '#12151f', '#0b0e15', '#161a26'],
        glow: { min: 5, max: 11 }
      },

      // Headlights coming on and fading out across the lot. The eyes slot
      // renders a pair of glowing dots side by side, which is exactly this.
      eyes: {
        enabled: true,
        spawnChance: 0.75,
        spawnInterval: { min: 2500, max: 7000 },
        colors: ['#fff6d5', '#ffd27a', '#c94b3a', '#ffe9b0', '#ff5f4d'],
        size: { min: 0.7, max: 1.3 },
        lifespan: 9000,
        opacity: { min: 0.5, max: 0.85 },
        glowIntensity: { min: 10, max: 20 },
        blinkChance: 0.15, // an occasional flicker, not blinking
        maxEyes: 10
      },

      // Retired. The slot draws walking-figure and ghost-blob silhouettes, not
      // the formless haze it was borrowed for, so at any opacity it read as
      // translucent bodies drifting over the lot.
      tombShadows: {
        enabled: false,
        density: { min: 5, max: 10 },
        shadowsPerSystem: { min: 2, max: 4 },
        speed: { min: 1, max: 2 },
        size: { min: 30, max: 80 },
        opacity: { min: 0.06, max: 0.16 },
        colors: ['#e8e4ff', '#d8d4f0', '#f0ecff', '#cfcae8', '#e0dcf5', '#bfb9d8'],
        flickerIntensity: { min: 1, max: 2 },
        walkingEffect: { enabled: true, intensity: 1 },
        lifespan: { min: 6000, max: 14000 }
      },

      // Retired: made redundant by the parked lot, which says the same thing
      // without a car sliding across the middle of the interface.
      //
      // lifespan has to outlast the crossing or the car is removed mid-screen:
      // it is killed by whichever comes first, age or leaving the viewport. At
      // the original speed 0.6 (12px/sec) and 28s it managed 336px - it
      // appeared, crept a third of a screen and evaporated in mid-air. 1.8 is
      // 36px/sec, so a 1920 screen takes ~63s, and 75s of life clears it.
      sarcophagus: {
        enabled: false,
        image: '/PhantomCar.webp',
        spawnChance: 0.45,
        spawnInterval: { min: 15000, max: 28000 },
        speed: 1.8,
        scale: 2.2,
        lifespan: 75000,
        opacity: 0.35,
        filter: 'drop-shadow(0 0 10px rgba(180,200,255,0.5)) brightness(0.7) contrast(1.25)',
        positioning: {
          startSide: 'random',
          movement: 'drift'
        }
      }
    }
  },

  // A wood at night with something in it. Deep forest black-green, cold eyes
  // between the trunks, figures moving in the fog, and a treeline closing in
  // along the foot of the frame. The interface is a camcorder tape -
  // scanlines, a wandering tracking band, a blinking REC - styled in App.css
  // under [data-theme="slasher_woods"], with the HUD coming from VhsOverlay.
  //
  // tombShadows is the point here, not a stand-in: its slot draws walking
  // figures and ghost shapes, which is exactly what should be moving between
  // the trees. Artwork spec for the killer: docs/THEME_ASSET_BRIEF_SLASHER.md.
  SLASHER_WOODS: {
    id: 'slasher_woods',
    name: 'Slasher Woods',
    description: 'Something is moving between the trees, and the tape is still running',
    titleIcon: {
      type: 'emoji',
      value: '🔪'
    },
    // Read by App: switches on the camcorder HUD.
    chrome: { vhs: true },
    background: {
      // Moonlit from above, black at the roots. Greener than the drive-in's
      // sky but far darker than Dark Forest - this is deep wood, not a glade.
      gradient: 'radial-gradient(ellipse at 50% 20%, #0f1a14 0%, #09110c 35%, #040806 70%, #010302 100%)',
      mistEffect: true,
      forestMist: true
    },
    creatures: {
      // Bats over the canopy. Classic flight; darkened hard, since there is
      // no light source up there to catch them.
      bats: {
        enabled: true,
        image: '/Bat.webp',
        spawnChance: 0.6,
        spawnInterval: { min: 2500, max: 6000 },
        speed: { min: 6.0, max: 9.0 },
        size: { min: 0.6, max: 1.0 },
        lifespan: 12000,
        opacity: 0.6,
        filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8)) brightness(0.45)',
        flutter: { amplitude: 15, frequency: 0.01 },
        wobble: { amplitude: 30, frequency: 0.005 }
      },

      // A cryptid in the trees, rarely. Mothman belongs in a wood far more
      // than it ever belonged over a drive-in lot.
      mothman: {
        enabled: true,
        image: '/Mothman.webp',
        spawnChance: 0.35,
        spawnInterval: { min: 30000, max: 60000 },
        speed: 24.0,
        scale: 3.5,
        lifespan: 30000,
        opacity: 0.55,
        filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.9)) brightness(0.5) contrast(1.2)',
        swooping: {
          primaryArc: 140,
          secondaryFlutter: 25,
          intensityVariation: { min: 0.2, max: 0.9 }
        }
      },

      // Eyes between the trunks. Fewer than Dark Forest, colder, and they stay
      // longer - something watching rather than something blinking.
      eyes: {
        enabled: true,
        spawnChance: 0.55,
        spawnInterval: { min: 4000, max: 9000 },
        colors: ['#e8f0f8', '#f2e9c9', '#d9e4ee', '#ffd9a0', '#c8d6e3'],
        size: { min: 0.6, max: 1.1 },
        lifespan: 14000,
        opacity: { min: 0.45, max: 0.8 },
        glowIntensity: { min: 6, max: 12 },
        blinkChance: 0.08,
        maxEyes: 6
      },

      // Figures in the fog. Dark forest greens and near-blacks, slower and
      // fainter than the tomb's, so they read as movement glimpsed rather
      // than shapes watched.
      tombShadows: {
        enabled: true,
        density: { min: 4, max: 8 },
        shadowsPerSystem: { min: 1, max: 3 },
        speed: { min: 0.6, max: 1.6 },
        size: { min: 40, max: 110 },
        opacity: { min: 0.12, max: 0.28 },
        colors: ['#06100a', '#0a1a10', '#040a07', '#0d1f14', '#081409', '#030705'],
        flickerIntensity: { min: 1, max: 2 },
        walkingEffect: { enabled: true, intensity: 2 },
        lifespan: { min: 9000, max: 18000 }
      },

      // The killer. One tall masked silhouette, drifting at the edge, almost
      // not there. Switched off until the artwork exists - pointing at a file
      // that is missing would 404 on every spawn. Spec in the brief; when
      // /Slasher.webp lands in public/, flip this to true and nothing else.
      sarcophagus: {
        enabled: false,
        image: '/Slasher.webp',
        spawnChance: 0.4,
        spawnInterval: { min: 25000, max: 50000 },
        // Must outlive its slowest crossing, which is the diagonal entry at
        // HALF speed: 1.4 is 28px/sec, so 14px/sec on that path, and 1380px
        // of vertical travel takes ~99s. 110s clears it; 60s would have
        // evaporated it mid-screen the moment it was switched on.
        speed: 1.4,
        scale: 2.0,
        lifespan: 110000,
        opacity: 0.3,
        filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.9)) brightness(0.35) contrast(1.4)',
        positioning: {
          startSide: 'random',
          movement: 'drift'
        }
      },

      // The wood itself, closing in along the foot of the frame. Drawn in
      // code, built once per theme, completely still.
      treeline: {
        enabled: true,
        rows: 2,
        height: { min: 90, max: 230 },
        colors: ['#0b140e', '#0f1a12', '#08100b', '#121f16']
      },

      // The set piece: a teen at full sprint with the killer walking behind
      // and still gaining. Drawn in code as two-frame silhouettes, so it ships
      // without artwork; `images` swaps in real sprites later (256x256, alpha,
      // facing LEFT - see the brief), with the same motion and gait bob.
      //
      // speed is px per 50ms tick: 7.0 is 140px/sec, a ~15s crossing on a
      // 1920 screen. The gap closes from 240px to 120px over the run, which
      // is the whole joke of the genre - he never runs, and he never loses.
      //
      // Cadence: the interval is picked once per spawn cycle and each tick
      // fires with spawnChance, so the expected wait is interval/chance. At
      // the original 45-90s and 0.5 that was one chase every 2-3 minutes;
      // 25-45s at 0.7 is ~50s of waiting plus the 15s run - about one a
      // minute, which is "occasional" without being "did I imagine that".
      chase: {
        enabled: true,
        spawnChance: 0.7,
        spawnInterval: { min: 25000, max: 45000 },
        speed: 7.0,
        gap: { start: 240, end: 120 },
        scale: 1.1,
        y: { min: 0.55, max: 0.78 }, // band of the viewport, as a fraction
        opacity: { teen: 0.85, slasher: 0.95 },
        // Relative height. He is a little taller than whoever he is after.
        // Both figures scale from their feet, so this grows him upward.
        stature: { teen: 1.0, slasher: 1.12 },
        lifespan: 30000,
        images: { teen: null, slasher: null }
      },

      // The jump scare: the mask filling the frame for under half a second,
      // every several minutes. Visual only - no audio, which autoplay blocks
      // anyway and which this app's users would not thank us for. One cut in,
      // one cut out, no strobe; off entirely under prefers-reduced-motion;
      // and never on arrival, so nobody is ambushed while the page is still
      // loading. Expected wait between scares is interval / spawnChance:
      // 5s-2min at 1.0 is exactly that, averaging about a minute.
      jumpScare: {
        enabled: true,
        firstDelay: 60000,
        spawnInterval: { min: 5000, max: 120000 },
        spawnChance: 1.0,
        hold: 450
      }
    }
  },

  // An old house at night: dark walnut panelling lit by one candle somewhere
  // above the frame, and the dead drifting through the hall as if it were
  // still theirs. Everything is drawn in code - the panelling is layered CSS
  // gradients in the background, the ghosts are SVG - so it ships without
  // artwork. The interface takes on the wood and a candle-brass accent in
  // App.css under [data-theme="haunted_house"].
  HAUNTED_HOUSE: {
    id: 'haunted_house',
    name: 'Haunted House',
    description: 'Dark wood, a guttering candle, and something drifting through the hall',
    titleIcon: {
      type: 'emoji',
      value: '🏚️'
    },
    background: {
      // Layered, back to front: the candle's glow from above centre, the
      // plank seams, a fine grain, and the walnut itself. One multi-layer
      // background on the same div every theme's gradient goes on.
      gradient: [
        'radial-gradient(ellipse at 50% 12%, rgba(255, 196, 110, 0.16) 0%, rgba(255, 170, 80, 0.05) 28%, rgba(0, 0, 0, 0) 55%)',
        'repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.42) 0px, rgba(0, 0, 0, 0.42) 2px, rgba(0, 0, 0, 0) 2px, rgba(0, 0, 0, 0) 164px)',
        'repeating-linear-gradient(90deg, rgba(255, 220, 170, 0.025) 0px, rgba(255, 220, 170, 0.025) 1px, rgba(0, 0, 0, 0) 1px, rgba(0, 0, 0, 0) 7px, rgba(0, 0, 0, 0.06) 7px, rgba(0, 0, 0, 0.06) 9px, rgba(0, 0, 0, 0) 9px, rgba(0, 0, 0, 0) 23px)',
        'linear-gradient(180deg, #1c120b 0%, #140d08 45%, #0b0704 100%)'
      ].join(', '),
      mistEffect: false
    },
    creatures: {
      bats: { enabled: false },
      mothman: { enabled: false },
      eyes: { enabled: false },
      tombShadows: { enabled: false },
      sarcophagus: { enabled: false },

      // The ghosts: pale sheeted figures drifting through the frame, a few
      // at a time, bobbing as they go. They fade up as they enter; most drift
      // out the far side, and some simply stop being there part way across.
      //
      // speed is px per 50ms tick: 1.6-2.6 is 32-52px/sec, a minute or so to
      // cross a 1920 screen. Lifespan must outlive the slowest, widest
      // crossing: 2880px at 32px/sec is ~90s.
      ghosts: {
        enabled: true,
        max: 3,
        spawnChance: 0.7,
        spawnInterval: { min: 5000, max: 10000 },
        speed: { min: 1.6, max: 2.6 },
        scale: { min: 0.8, max: 1.4 },
        y: { min: 0.15, max: 0.65 },          // band of the viewport, as a fraction
        bob: { amplitude: 16, period: 4200 }, // px, ms per cycle
        opacity: 0.78,
        fadeIn: 1500,                          // ms to fade up on entry
        vanish: { chance: 0.4, over: 2000 },  // some stop existing mid-hall
        lifespan: 100000
      },

      // The room itself. Scenery, built once per theme and fixed to the
      // viewport behind the interface; the only thing on a timer is the
      // lightning. All drawn in code, in AnimatedBackground and App.css.

      // Two tall windows, high on the wall, with a storm going on outside:
      // rain in two layers, clouds that crawl, and lightning now and then
      // that lights the glass and, faintly, the room. Positions are fractions
      // of the viewport; each is anchored by left or right.
      windows: {
        enabled: true,
        positions: [{ left: 0.05, top: 0.1 }, { right: 0.05, top: 0.1 }],
        lightning: {
          interval: { min: 9000, max: 24000 },  // between strikes
          hold: 600                              // ms the flash sequence runs
        },
        // Something at the glass. Now and then a bat-like face with yellow
        // eyes leans in from the right edge of the left window, tilted, looks
        // in, and withdraws. Outside the pane - behind the bars and the rain.
        peeker: {
          enabled: true,
          window: 0,                             // which window, by index
          interval: { min: 20000, max: 45000 },  // between visits
          hold: 3400                             // ms from leaning in to gone
        }
      },

      // A fire in the grate, bottom corner, with its light thrown up the
      // wall and across the floor.
      fireplace: {
        enabled: true,
        side: 'left'
      },

      // Candles floating where no hand holds them, each at its own slow bob.
      // [x, y, size]: fractions of the viewport, and a multiplier.
      candles: {
        enabled: true,
        positions: [
          [0.22, 0.16, 1.0],
          [0.31, 0.32, 0.8],
          [0.45, 0.09, 0.9],
          [0.58, 0.26, 1.1],
          [0.71, 0.13, 0.85],
          [0.85, 0.3, 0.95]
        ]
      },

      // A tall bookcase against the far wall, opposite the fire, and a
      // wingback armchair drawn up beside the hearth. Scenery, like the rest
      // of the room; the books are laid out from the index, so the shelves
      // look the same every visit.
      bookshelves: {
        enabled: true,
        side: 'right',
        shelves: 5
      },
      armchair: {
        enabled: true,
        left: 0.27   // fraction of the viewport; beside the fire, facing it
      },

      // A raven, perched: on top of the bookcase, or on the mantel on a
      // phone, where the bookcase is not there. Where it sits is the
      // stylesheet's business (it follows the breakpoints); it turns its
      // head now and then.
      raven: {
        enabled: true
      },

      // Over the mantel, a portrait of an old man with severe features, in
      // a gilt frame, lit from below by the fire. Hung from the fireplace's
      // own width, so it stays over the hearth at every viewport size.
      portrait: {
        enabled: true
      },

      // Two more on the wall to his right, hung at different heights: a
      // young woman with the same severe cast, and a knight with the visor
      // down. `left` and `bottom` are fractions of the viewport; `width` is
      // a multiple of the fireplace's width, like the first portrait. The
      // knight hangs over the secret door. On a signed-in desktop the
      // calendar grid covers this part of the wall, as it covers the rest.
      portraits: {
        enabled: true,
        items: [
          { kind: 'woman', left: 0.36, bottom: 0.38, width: 0.36 },
          { kind: 'knight', left: 0.53, bottom: 0.29, width: 0.42 }
        ]
      },

      // Between the chair and the bookcase, a door in the panelling that is
      // not supposed to be there. Now and then it swings open onto darkness,
      // two red eyes light in it, and a clawed hand comes round the jamb and
      // takes hold. Then it closes again. Position is a fraction of the
      // viewport; intervals in ms.
      secretDoor: {
        enabled: true,
        left: 0.55,
        interval: { min: 25000, max: 50000 },  // between openings
        swing: 1600,                            // ms the panel takes to swing; matches the CSS transition
        sink: 700,                              // ms it takes to sink back into the wall once shut
        openFor: { min: 6000, max: 9000 },      // how long it stands open, retreat included
        retreat: 1600                           // ms at the end: the hand withdraws, the eyes go out
      },

      // The cloaked man: now and then he crosses the floor with a candelabra
      // held out before him, unhurried, twice the height of the fireplace.
      // Drawn in code; his height is tied to the fireplace's, so the ratio
      // holds at every viewport width.
      //
      // speed is px per 50ms tick: 1.4 is 28px/sec, over a minute to cross a
      // 1920 screen. Lifespan must outlive the widest crossing: 3160px at
      // 28px/sec is ~113s.
      cloakedMan: {
        enabled: true,
        spawnChance: 0.6,
        spawnInterval: { min: 40000, max: 80000 },
        speed: 1.4,
        stature: 2,          // in fireplace heights
        opacity: 0.96,
        lifespan: 150000
      }
    }
  },

  MINIMAL: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean background with subtle mist effect only',
    titleIcon: {
      type: 'emoji',
      value: '🌙'
    },
    background: {
      gradient: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f0f23 100%)',
      mistEffect: true
    },
    creatures: {
      bats: { enabled: false },
      mothman: { enabled: false }
    }
  }
};

// Default theme
export const DEFAULT_THEME = THEMES.HORROR_CLASSIC;

// Theme management utilities
export const getThemeById = (themeId) => {
  return Object.values(THEMES).find(theme => theme.id === themeId) || DEFAULT_THEME;
};

// A theme named in the URL, for looking at one without changing anyone's
// setting: /?theme=ancient_tomb. It is never saved, and the global setting
// is not applied over it, so an admin can preview a theme on the live site
// while every participant keeps seeing the current one. Unknown ids are
// ignored and the normal preference applies.
export const previewThemeFromUrl = () => {
  if (typeof window === 'undefined') return null;
  try {
    const id = new URLSearchParams(window.location.search).get('theme');
    if (!id) return null;
    return Object.values(THEMES).find((theme) => theme.id === id) || null;
  } catch (error) {
    return null;
  }
};

export const getAllThemes = () => {
  return Object.values(THEMES);
};

export const getAvailableThemes = () => {
  return Object.values(THEMES);
};

// Theme storage utilities
export const saveThemePreference = (themeId) => {
  try {
    localStorage.setItem('app_theme', themeId);
    console.log('✅ Theme preference saved:', themeId);
  } catch (error) {
    console.warn('⚠️ Failed to save theme preference:', error);
  }
};

export const loadThemePreference = () => {
  try {
    const savedTheme = localStorage.getItem('app_theme');
    if (savedTheme) {
      const theme = getThemeById(savedTheme);
      console.log('🎨 Loaded theme preference:', theme.name);
      return theme;
    }
  } catch (error) {
    console.warn('⚠️ Failed to load theme preference:', error);
  }
  
  console.log('🎨 Using default theme:', DEFAULT_THEME.name);
  return DEFAULT_THEME;
};

/**
 * Load theme preference with global settings support
 * Checks global theme (Supabase) first, then localStorage, then default
 */
export const loadThemePreferenceWithGlobal = async () => {
  try {
    // First, try to load global theme setting (admin-controlled)
    const { getGlobalThemeAppLevel } = await import('../services/appLevelSettings');
    const globalThemeResult = await getGlobalThemeAppLevel();
    
    if (globalThemeResult.data) {
      const globalTheme = getThemeById(globalThemeResult.data);
      if (globalTheme) {
        console.log('🎨 Loaded global theme setting:', globalTheme.name);
        return globalTheme;
      }
    }
  } catch (error) {
    console.warn('⚠️ Failed to load global theme setting:', error);
  }

  // Fallback to local preference
  try {
    const savedTheme = localStorage.getItem('app_theme');
    if (savedTheme) {
      const theme = getThemeById(savedTheme);
      console.log('🎨 Loaded local theme preference:', theme.name);
      return theme;
    }
  } catch (error) {
    console.warn('⚠️ Failed to load local theme preference:', error);
  }
  
  console.log('🎨 Using default theme:', DEFAULT_THEME.name);
  return DEFAULT_THEME;
};
